import { useContext, useMemo } from 'react';
import { DateTime } from 'luxon';
import { bookingState } from '../client-state/booking-state-context';
import { BookingLocalization, FeatureName } from '../../../Utils/graphql-sdk';
import useTypeSafeLocalize from '../../../Hooks/use-type-safe-localize';
import { checkFeatureFlag, getDurationString } from '../../../Utils/utils';
import arrangeInIntervals from '../../../ui-components/timeslots/arrange-in-intervals';
import Timeslots, { ParsedTimeslot } from '../../../ui-components/timeslots/timeslots';
import useBookingPrices from '../../../Hooks/use-booking-prices';
import useBookingTracking from '../../../Hooks/useBookingTracking';
import { LocalizationCategory, ProductLocalization } from '../../../localizations/types';
import { bookingActionTypes } from '../client-state/reducers/bookingReducer';
import useBookingShop from '../server-data/use-booking-shop';
import useTimeslots from '../../../Hooks/use-timeslots';
import { useGetCustomBookingLocalization } from '../../../Hooks/use-get-custom-booking-localization';

const TimeslotsWithData = () => {
  const { data: shop } = useBookingShop();
  const { state, dispatch } = useContext(bookingState);
  const localize = useTypeSafeLocalize();
  const getCustomBookingLocalization = useGetCustomBookingLocalization();
  const { calculateBookingPrices } = useBookingPrices();
  const track = useBookingTracking();

  const timeslotsHelpText =
    getCustomBookingLocalization(BookingLocalization.ChooseADate) ||
    localize(LocalizationCategory.product, ProductLocalization.selectDateToViewTimeslots);

  const localizedTimeFormat = checkFeatureFlag(
    shop || {},
    FeatureName.LocalizedTimeDisplay
  );

  const { data: timeslots, isFetching, error } = useTimeslots();

  const intervals = useMemo(() => {
    if (!timeslots) return [];

    let slots: ParsedTimeslot[] = timeslots.map((slot) => {
      const startTime = DateTime.fromISO(slot.dateTime).setZone(shop?.timezoneName);

      const vacanciesObject = slot.vacancies.find(
        (v) => v.duration === getDurationString(state.booking.duration)
      ) || {
        vacancies: 0,
        price: 0,
      };

      const { vacancies, price } = vacanciesObject;

      const bookable = vacancies >= state.booking.boatCount;

      return {
        startTime,
        bookable,
        vacancies,
        price,
        priceModelId: slot.priceModelId,
        isBlockedByMinimumNotice: Boolean(slot.isBlockedByMinimumNotice),
      };
    });

    return arrangeInIntervals(slots);
  }, [timeslots, state.booking.boatCount, state.booking.duration, shop?.timezoneName]);

  const handleChange = (timeslot: ParsedTimeslot) => {
    const bookingDate = timeslot.startTime.toISO();
    const startTime = timeslot.startTime.toFormat('HH:mm');
    const endTime = timeslot.startTime
      .plus({ hours: state.booking.duration })
      .toFormat('HH:mm');

    if (bookingDate) {
      dispatch({ type: bookingActionTypes.UPDATE_DATE, payload: bookingDate });
      dispatch({ type: bookingActionTypes.UPDATE_START_TIME, payload: startTime });
      dispatch({ type: bookingActionTypes.UPDATE_END_TIME, payload: endTime });
      dispatch({
        type: bookingActionTypes.UPDATE_PRICE_MODEL_ID,
        payload: timeslot.priceModelId,
      });

      const prices = calculateBookingPrices(timeslot.priceModelId);

      track({
        type: 'ADD_TO_CART',
        product: {
          ...state.booking,
          price: prices.fullPrice,
        },
      });

      const cancellationProtection = document.getElementById('cancellation-protection');
      if (cancellationProtection) {
        setTimeout(() => {
          cancellationProtection.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 350);
      }
    }
  };

  return (
    <Timeslots
      bookingDate={state.booking.date}
      boatCount={state.booking.boatCount}
      duration={state.booking.duration}
      intervals={intervals}
      onChange={handleChange}
      helpText={timeslotsHelpText}
      loading={isFetching}
      error={error instanceof Error ? error.message : undefined}
      enabled={true}
      vacancyThreshold={shop?.configuration?.lowAvailabilityAlertThreshold}
      localizedTimeFormat={localizedTimeFormat}
    />
  );
};

export default TimeslotsWithData;
